'use client';

import { useRouter } from 'next/navigation';
import { Fragment, useEffect, useState } from 'react';

import {
  CloseIcon,
  CTA,
  OrderDetailSectionTitle,
  PageHeader
} from '@/components/atomic/atoms';
import {
  OrderDeliveryDetails,
  OrderEventInfoMobile,
  OrderEventInfoWeb,
  OrderItemCard,
  OrderItemDetail,
  OrderSummary
} from '@/components/atomic/molecules';
import { ORDER_STATUS } from '@/config/common';
import { ORDER_ACKNOWLEDGEMENT_STATUS } from '@/config/searchkit/orderList';
import { isTodayOrPast } from '@/lib/dateAndTime';
import { GetUiDeviceSurface } from '@/lib/screenResolution';
import {
  getTransformedProductMediaList,
  updatePartnerOrderAcknowledgement
} from '@/services/order.service';

const OrderDetail = ({
  deliveryDate,
  deliveryTime,
  idOfOrderItemInDetailView,
  partnerPriceWithAdditionalCharge,
  pickupDate,
  pickupTime,
  productMediaList,
  productName,
  productNotes,
  quantity,
  setIdOfOrderItemInDetailView,
  status,
  unitPartnerPrice
}) => (
  <>
    {idOfOrderItemInDetailView && (
      <OrderItemDetail
        {...{
          deliveryDate,
          deliveryTime,
          partnerPriceWithAdditionalCharge,
          pickupDate,
          pickupTime,
          productMediaList,
          productName,
          productNotes,
          quantity,
          setIdOfOrderItemInDetailView,
          status,
          unitPartnerPrice
        }}
      />
    )}
  </>
);

const OrderItemCardList = ({
  idOfOrderItemInDetailView,
  orderItems,
  setIdOfOrderItemInDetailView,
  status
}) => (
  <Fragment>
    <OrderDetailSectionTitle
      {...{
        title: 'Event Requirements',
        setIdOfOrderItemInDetailView,
        status
      }}
    />

    {orderItems.map(
      ({
        deliveryDate,
        deliveryTime,
        id,
        orderItemMedia,
        partnerPriceWithAdditionalCharge,
        pickupDate,
        pickupTime,
        productMedia,
        productName,
        productNotes,
        quantity,
        showProductMedia,
        unitPartnerPrice
      }) => {
        const productMediaList = getTransformedProductMediaList({
          orderItemMedia,
          productMedia,
          showProductMedia
        });

        return idOfOrderItemInDetailView === id ? (
          <OrderDetail
            key={id}
            {...{
              deliveryDate,
              deliveryTime,
              idOfOrderItemInDetailView,
              partnerPriceWithAdditionalCharge,
              pickupDate,
              pickupTime,
              productMediaList,
              productName,
              productNotes,
              quantity,
              setIdOfOrderItemInDetailView,
              status,
              unitPartnerPrice
            }}
          />
        ) : (
          <OrderItemCard
            key={id}
            {...{
              deliveryDate,
              deliveryTime,
              id,
              pickupDate,
              pickupTime,
              productMediaList,
              productName,
              quantity,
              setIdOfOrderItemInDetailView,
              status
            }}
          />
        );
      }
    )}
  </Fragment>
);

const CloseOrderDetailMobile = ({ router }) => (
  <CloseIcon
    {...{
      className:
        'absolute top-2 left-100 mr-0 flex justify-end w-full -ml-3 mt-2',
      icon: 'close-with-border-white.svg',
      iconClassName: 'cursor-pointer',
      iconHeight: 18,
      iconWidth: 18,
      onClick: () => router.back()
    }}
  />
);

const CloseOrderDetailWeb = ({ router }) => (
  <div className='md:w-50 relative mx-auto bg-slate-950 flex justify-end'>
    <CloseIcon
      {...{
        className: 'hidden md:flex justify-end absolute -mr-8 top-7',
        icon: 'close-red.svg',
        iconClassName: 'cursor-pointer',
        iconHeight: 24,
        iconWidth: 24,
        onClick: () => router.back()
      }}
    />
  </div>
);

const OrderEventInfoMap = {
  mobile: OrderEventInfoMobile,
  web: OrderEventInfoWeb
};

const CloseOrderDetailMap = {
  mobile: CloseOrderDetailMobile,
  web: CloseOrderDetailWeb
};

const OrderConfirmationCTAPair = ({
  eventDate,
  orderDetails,
  partnerOrderAcknowledgement,
  partnerUser,
  setLoading,
  setOrderDetails,
  setShowToast,
  status
}) => {
  const [isFooterVisible, setIsFooterVisible] = useState(false);

  useEffect(() => {
    const footerEl = document.querySelector('footer');
    if (!footerEl) return () => {};

    const observer = new IntersectionObserver(
      ([entry]) => setIsFooterVisible(entry.isIntersecting),
      { root: null, threshold: 0.1 }
    );

    observer.observe(footerEl);
    return () => observer.disconnect();
  }, []);

  const disableCTA =
    isTodayOrPast(eventDate) || status === ORDER_STATUS.CANCELLED.value;

  const CTAList = [
    {
      buttonColor: `border-brand text-brand bg-white border h-11 px-2 md:py-4 text-sm font-medium w-full`,
      children: ORDER_ACKNOWLEDGEMENT_STATUS.NEED_ASSISTANCE.label,
      disabled:
        disableCTA ||
        partnerOrderAcknowledgement ===
          ORDER_ACKNOWLEDGEMENT_STATUS.NEED_ASSISTANCE.value,
      onClick: () =>
        updatePartnerOrderAcknowledgement({
          orderDetails,
          partnerOrderAcknowledgement:
            ORDER_ACKNOWLEDGEMENT_STATUS.NEED_ASSISTANCE.value,
          partnerUser,
          setLoading,
          setOrderDetails,
          setShowToast
        })
    },
    {
      buttonColor: `bg-brand-gradient text-white h-11 px-6 md:py-4 text-sm w-full`,
      children: ORDER_ACKNOWLEDGEMENT_STATUS.ACCEPT_ORDER.label,
      className: `font-medium px-3`,
      disabled: disableCTA,
      onClick: () =>
        updatePartnerOrderAcknowledgement({
          orderDetails,
          partnerOrderAcknowledgement:
            ORDER_ACKNOWLEDGEMENT_STATUS.ACCEPT_ORDER.value,
          partnerUser,
          setLoading,
          setOrderDetails,
          setShowToast
        })
    }
  ];

  return (
    <div
      className={`${
        isFooterVisible ? 'relative' : 'fixed bottom-0 left-0 right-0'
      } bg-white shadow-lg border-t md:px-4 flex justify-between gap-4 z-50`}
    >
      <div
        className={`flex md:gap-5 gap-2 justify-center my-1 md:my-3 mx-1 md:mx-6 w-full md:w-50 md:mx-auto`}
      >
        {CTAList.map(
          ({ show = true, ...props }, index) =>
            show && (
              <div
                key={index}
                className='w-44 md:w-1/2'
              >
                <CTA {...props} />
              </div>
            )
        )}
      </div>
    </div>
  );
};

const OrderDetailOrganism = ({
  orderDetails,
  partnerUser,
  setLoading,
  setOrderDetails,
  setShowToast
}) => {
  const {
    eventContactName,
    eventDate,
    eventInfo,
    exactAddress,
    googleLocationPin,
    orderItems = [],
    orderSummary = {},
    partnerOrderAcknowledgement: defaultPartnerOrderAcknowledgement,
    showOrderDeliveryDetails = false,
    status,
    user: { mobile } = {}
  } = orderDetails || {};
  const router = useRouter();
  const surface = GetUiDeviceSurface();
  const OrderEventInfo = OrderEventInfoMap[surface];
  const CloseOrderDetail = CloseOrderDetailMap[surface];

  const [idOfOrderItemInDetailView, setIdOfOrderItemInDetailView] =
    useState(null);

  const { status: partnerOrderAcknowledgement = null } =
    defaultPartnerOrderAcknowledgement || {};

  const renderOrderConfirmationCTAPair =
    !partnerOrderAcknowledgement ||
    partnerOrderAcknowledgement ===
      ORDER_ACKNOWLEDGEMENT_STATUS.NEED_ASSISTANCE.value;

  return (
    <div>
      <div className='relative'>
        <CloseOrderDetail {...{ router }} />
        <div className='block md:hidden'>
          <PageHeader
            {...{
              className: 'mt-3',
              height: 'h-40',
              isListView: false,
              status,
              title: 'Order Details'
            }}
          />
        </div>
      </div>
      <div className='w-full md:w-50 md:mx-auto'>
        <OrderEventInfo
          {...{
            eventInfo,
            exactAddress,
            partnerOrderAcknowledgement,
            status
          }}
        />
        {showOrderDeliveryDetails && (
          <OrderDeliveryDetails
            {...{
              eventContactName,
              exactAddress,
              googleLocationPin,
              mobile,
              status
            }}
          />
        )}
        <OrderItemCardList
          {...{
            idOfOrderItemInDetailView,
            orderItems,
            setIdOfOrderItemInDetailView,
            status
          }}
        />
        <OrderSummary {...{ orderSummary, status }} />
        {renderOrderConfirmationCTAPair && (
          <OrderConfirmationCTAPair
            {...{
              eventDate,
              orderDetails,
              partnerOrderAcknowledgement,
              partnerUser,
              renderOrderConfirmationCTAPair,
              setLoading,
              setOrderDetails,
              setShowToast,
              status
            }}
          />
        )}
      </div>
    </div>
  );
};

export default OrderDetailOrganism;
